/**
 * UIContext — app-level, wraps all authenticated routes.
 *
 * OWNS
 *   Transient, cross-cutting UI state that multiple unrelated components need to share:
 *   • isNavbarCollapsed / setIsNavbarCollapsed — sidebar expand/collapse
 *   • colorMode / toggleColorMode — app-wide dark/light theme (to be added here when
 *     implementing theming, so all components read from one place instead of calling
 *     Chakra's useColorMode directly)
 *
 * BOUNDARIES
 *   • Intentionally lightweight: holds NO domain data, NO API calls, NO user identity.
 *   • The rule: a piece of state belongs here only if it is purely visual AND must be
 *     readable/writable from two or more unrelated subtrees (e.g. both the Navbar and the
 *     ChatContainer need to collapse the sidebar when a message is sent; both the Navbar
 *     and the Settings page need to read/write the color mode).
 *   • Per-page or per-feature UI state (e.g. artifact panel open, modal open) does NOT
 *     belong here — it lives as local state in the relevant component.
 *
 * CONSUMERS
 *   • Navbar (reads/writes isNavbarCollapsed; will read colorMode for theme icon)
 *   • ChatContainer (writes isNavbarCollapsed=true when user sends a message)
 *   • Any component that needs app-wide theme state once colorMode is implemented
 */
import React, { createContext, useContext, useMemo, useState } from "react";

interface UIContextType {
	isNavbarCollapsed: boolean;
	setIsNavbarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

export const UIContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [isNavbarCollapsed, setIsNavbarCollapsed] = useState(false);

	const value = useMemo<UIContextType>(() => ({
		isNavbarCollapsed,
		setIsNavbarCollapsed,
	}), [isNavbarCollapsed]);

	return (
		<UIContext.Provider value={value}>
			{children}
		</UIContext.Provider>
	);
};

export const useUIContext = () => {
	const context = useContext(UIContext);
	if (!context) {
		throw new Error("useUIContext must be used within a UIContextProvider");
	}
	return context;
};
